import { inject, Injectable } from '@angular/core';

import { CreateMutationOptions, CreateQueryOptions } from '@tanstack/angular-query-experimental';
import { UserProfileType } from '@type/user.type';
import { filter, map, Observable, Subject } from 'rxjs';

import { ApiQueryService } from '@services/helper/api-queries.service';
import { ApiService } from '@services/helper/api-wrapper.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly _api = inject(ApiService);
  private readonly _apiQuery = inject(ApiQueryService);

  /* ── Private API methods ─────────────────────────────────────── */

  /** GET /api/roles?per_page=X */
  private getAllUsersRoles(signal?: AbortSignal): Observable<any> {
    return this._api.get(`/roles?per_page=1000`, {}, {}, signal);
  }

  /**
   * GET /api/users?per_page=X&page=Y[&company_id=Z][&search=foo]
   *
   * `search` is a free-text query the backend matches against display name /
   * email / agency code etc. Sent through to the API so the search hits the
   * full database, not just the current page.
   */
  private getAllUsersPaginated(
    page: number,
    perPage: number,
    company_id?: number | null,
    search?: string | null,
    request_access?: number | null,
    is_active?: number | null,
    sort_by?: string | null,
    sort_as?: 'asc' | 'desc' | null,
    role_id?: number | null,
    project_id?: number | null,
    signal?: AbortSignal,
  ): Observable<any> {
    const params: string[] = [`per_page=${perPage}`, `page=${page}`];
    if (company_id) params.push(`company_id=${company_id}`);
    const trimmed = (search ?? '').trim();
    if (trimmed) params.push(`search=${encodeURIComponent(trimmed)}`);
    if (request_access === 0 || request_access === 1)
      params.push(`request_access=${request_access}`);
    if (is_active === 0 || is_active === 1) params.push(`is_active=${is_active}`);
    if (sort_by) params.push(`sort_by=${sort_by}`);
    if (sort_as) params.push(`sort_as=${sort_as}`);
    if (role_id) params.push(`role_id=${role_id}`);
    if (project_id) params.push(`project_id=${project_id}`);
    return this._api.get(`/users?${params.join('&')}`, {}, {}, signal);
  }

  /** GET /api/users?per_page=X&page=Y&company_id=Z */
  private getUsersByCompanyIdPaginated(
    companyId: number,
    page: number,
    perPage: number,
    signal?: AbortSignal,
  ): Observable<any> {
    return this._api.get(
      `/users?per_page=${perPage}&page=${page}&company_id=${companyId}`,
      {},
      {},
      signal,
    );
  }

  /** GET /api/users/:id */
  private getUserById(id: number, signal?: AbortSignal): Observable<any> {
    return this._api.get(`/users/${id}`, {}, {}, signal);
  }

  /** GET /api/users?per_page=5000&company_id=X — all users by agency (no pagination) */
  private getUsersByAgencyId(agencyId: number, signal?: AbortSignal): Observable<any> {
    return this._api.get(`/users?per_page=5000&company_id=${agencyId}&role_id=3`, {}, {}, signal);
  }

  /** GET /api/users?per_page=5000&role=agency+manager — all agency managers */
  private getAllAgencyManagers(signal?: AbortSignal): Observable<any> {
    return this._api.get('/users?per_page=5000&role_id=3', {}, {}, signal);
  }

  /** GET /api/users?per_page=5000&role=developer — all developers */
  private getAllUsersDeveloper(signal?: AbortSignal): Observable<any> {
    return this._api.get('/users?per_page=5000&role_id=1', {}, {}, signal);
  }

  /** GET /api/users?per_page=5000&company_id=X&include_developers=1 — users + developers by agency */
  private getDeveloperUsers(signal?: AbortSignal): Observable<any> {
    return this._api.get('/users?per_page=5000&role_id=1', {}, {}, signal);
  }

  /** POST /api/users */
  private createUser(payload: UserProfileType): Observable<any> {
    return this._api.post(`/users`, payload);
  }

  /** PUT /api/users/:id */
  private updateUser(id: number, payload: UserProfileType): Observable<any> {
    return this._api.put(`/users/${id}`, payload);
  }

  /** PUT /api/users/:id/approve */
  private approveUserById(id: number): Observable<any> {
    return this._api.patch(`/users/${id}/approve`, {});
  }

  /** PUT /api/me */
  private updateMe(payload: UserProfileType): Observable<any> {
    return this._api.put('/me', payload);
  }

  /** DELETE /api/users/:id */
  private deleteUser(id: number): Observable<any> {
    return this._api.delete(`/users/${id}`);
  }

  /** POST /api/users/import-csv */
  /** POST /api/users/import */
  private importUsersCsv(params: { csv: File }): Observable<any> {
    const formData = new FormData();
    formData.append('file', params.csv); // 👈 field name is 'file' (not 'csv')

    const cancel$ = new Subject<void>();
    return this._api.uploadFileWithProgress('/users/import', formData, cancel$).pipe(
      filter((event: any) => event.type === 'response'),
      map((event: any) => event.body),
    );
  }

  /** POST /api/users/export-csv */
  /** GET /api/users/export */
  /** GET /api/users/export */
  private exportUsersCsv(signal?: AbortSignal): Observable<any> {
    return this._api.get('/users/export', {}, {}, signal, 'blob');
  }

  /* ── Public query wrappers ───────────────────────────────────── */

  public queryGetAllUsersPaginated(
    page: () => number,
    perPage: () => number,
    company_id: () => number | undefined | null,
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
    search?: () => string | null | undefined,
    request_access?: () => number | null | undefined,
    is_active?: () => number | null | undefined,
    sort_by?: () => string | null | undefined,
    sort_as?: () => 'asc' | 'desc' | null | undefined,
    role_id?: () => number | null | undefined,
    project_id?: () => number | null | undefined,
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) =>
        this.getAllUsersPaginated(
          page(),
          perPage(),
          company_id(),
          search?.() ?? null,
          request_access?.() ?? null,
          is_active?.() ?? null,
          sort_by?.() ?? null,
          sort_as?.() ?? null,
          role_id?.() ?? null,
          project_id?.() ?? null,
          signal,
        ),
      () => [
        this._apiQuery.USER_LIST,
        'paginated',
        perPage(),
        page(),
        company_id() ?? '',
        (search?.() ?? '').trim().toLowerCase(),
        request_access?.() ?? 0,
        is_active?.() ?? 'all',
        sort_by?.() ?? '',
        sort_as?.() ?? '',
        role_id?.() ?? '',
        project_id?.() ?? '',
      ],
      {},
      config,
    );
  }

  public queryGetAllUsersRoles(
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getAllUsersRoles(signal),
      [this._apiQuery.USER_ROLES],
      {},
      { ...config, staleTime: Infinity, gcTime: Infinity },
    );
  }

  public queryGetUsersByCompanyIdPaginated(
    companyId: () => number,
    page: () => number,
    perPage: () => number,
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getUsersByCompanyIdPaginated(companyId(), page(), perPage(), signal),
      () => [this._apiQuery.USER_LIST, 'by-company', companyId(), perPage(), page()],
      {},
      config,
    );
  }

  public queryGetUserById(
    id: number | (() => number),
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getUserById(typeof id === 'function' ? id() : id, signal),
      () => [this._apiQuery.USER_BY_ID, typeof id === 'function' ? id() : id],
      {},
      config,
    );
  }

  /** GET /api/users?per_page=1000&company_id=X */
  public queryGetUsersByAgencyId(
    agencyId: number | (() => number),
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) =>
        this.getUsersByAgencyId(typeof agencyId === 'function' ? agencyId() : agencyId, signal),
      () => [
        this._apiQuery.ALL_USERS_BY_AGENCY_ID,
        typeof agencyId === 'function' ? agencyId() : agencyId,
      ],
      {},
      config,
    );
  }

  /** GET /api/users?per_page=1000&role=agency+manager */
  public queryGetAllAgencyManager(
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getAllAgencyManagers(signal),
      [this._apiQuery.ALL_AGENCY_MANAGER],
      {},
      config,
    );
  }

  /** GET /api/users?per_page=1000&role=developer */
  public queryGetAllUsersDeveloper(
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getAllUsersDeveloper(signal),
      [this._apiQuery.ALL_USERS_DEVELOPER],
      {},
      config,
    );
  }

  /** GET /api/users?per_page=1000&company_id=X&include_developers=1 */
  public queryGetUsersAndDeveloperByAgencyId(
    config: Partial<CreateQueryOptions> | (() => Partial<CreateQueryOptions>) = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getDeveloperUsers(signal),
      () => [this._apiQuery.ALL_DEVELOPER_USERS],
      {},
      config,
    );
  }

  /* ── Public mutation wrappers ────────────────────────────────── */

  /** PUT /api/users/:id */
  public updateUserMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { id: number; payload: any }) => this.updateUser(params.id, params.payload),
      config,
    );
  }

  /** PUT /api/users/:id/approve */
  public approveUserStatusMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation((id: number) => this.approveUserById(id), config);
  }

  /** POST /api/users */
  public createUserMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { payload: UserProfileType }) => this.createUser(params.payload),
      config,
    );
  }

  /** PUT /api/me */
  public updateMeMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation((payload: any) => this.updateMe(payload), config);
  }

  /** DELETE /api/users/:id */
  public deleteUserMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { id: number }) => this.deleteUser(params.id),
      config,
    );
  }

  public importUsersCsvMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { csv: File }) => this.importUsersCsv(params),
      config,
    );
  }

  public exportUsersCsvMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(() => this.exportUsersCsv(), config);
  }
}
